import { formatDate } from "common/service/CommonService";
import { GetUserData } from "common/service/CommonService";
import {
  ContainerDeductionState,
  ContainerItem,
  DispensingRecord,
  DispensingSavePayload,
  DispensingUpdatePayload,
} from "./dispensing.types";

/**
 * Calculates total quantity needed based on subject count, per subject quantity, and extra quantity.
 */
export const calculateTotalQuantityNeeded = (
  subjectCount: number,
  perSubjectQty: number | null,
  extraQuantity: number | null
): number | null => {
  const perSub = Number(perSubjectQty) || 0;
  const extra = Number(extraQuantity) || 0;
  const total = subjectCount * perSub + extra;
  return total > 0 ? total : null;
};

/**
 * Calculates net usable quantity across all selected containers after deductions.
 */
export const calculateNetUsableContainerQuantity = (
  selectedContainerDetails: ContainerItem[],
  deductions: ContainerDeductionState
): number => {
  return selectedContainerDetails.reduce((acc: number, c) => {
    const initial = Number(c.initialquantity || 0);
    const ded = Number(deductions[c.containernumber]?.deductedqty || 0);
    const finalQty = Math.max(0, initial - ded);
    return acc + finalQty;
  }, 0);
};

/**
 * Calculates total deducted quantity across all selected containers.
 */
export const calculateTotalDeductedQuantity = (
  selectedContainerDetails: ContainerItem[],
  deductions: ContainerDeductionState
): number => {
  return selectedContainerDetails.reduce((acc: number, c) => {
    const ded = Number(deductions[c.containernumber]?.deductedqty || 0);
    return acc + ded;
  }, 0);
};

/**
 * Calculates total initial available quantity across selected containers.
 */
export const calculateTotalInitialQuantity = (
  selectedContainerDetails: ContainerItem[]
): number => {
  return selectedContainerDetails.reduce(
    (acc: number, c) => acc + Number(c.initialquantity || 0),
    0
  );
};

/**
 * Maps raw API table rows into strongly-typed DispensingRecord objects.
 */
export const mapDispensingRecord = (row: any): DispensingRecord => {
  const dispensedByStr = row.dispensedby ?? "";
  const initials =
    row.dispensedbyinitials ??
    (dispensedByStr
      ? dispensedByStr
          .split(" ")
          .map((n: string) => n[0])
          .join("")
          .toUpperCase()
          .slice(0, 2)
      : "?");

  const subjectRangeStr =
    row.subjectrange ??
    `${String(row.startsubjectno ?? "").padStart(2, "0")} - ${String(row.endsubjectno ?? "").padStart(2, "0")}`;

  return {
    dispensingid: row.dispensingid,
    logid: row.logid,
    studyprotocol: row.studyprotocol ?? row.studycode ?? "",
    studyname: row.studyname ?? row.studytypetext ?? "",
    subjectrange: subjectRangeStr,
    subjectcount: row.subjectcount ?? row.totalquantityneeded ?? 0,
    dispenseddate: row.dispenseddate ?? formatDate(row.dispensingdate),
    dispensedtime: row.dispensedtime ?? row.dispensingtime ?? "",
    dispensedby: dispensedByStr,
    dispensedbyinitials: initials,
    isunassigned: row.isunassigned ?? !row.dispensedby,
    total_count: row.total_count ?? 0,
    isverified: row.isverified ?? false,
    studyid: row.studyid ?? 0,
    productname: row.productname || "",
    ipcategory: row.ipcategory || "",
    batchlotno: row.batchlotno || "",
    totalquantity: row.totalquantityneeded ?? 0,
    qtypersubject: row.quantitypersubject ?? 0,
    verifiedbyname: row.verifiedbyname || "",
    verifieddate: row.verifieddate || "",
    expirydate: row.expirydate || "",
    availablequantity: row.availablequantity || 0,
    sponsorname: row.sponsorname || "",
  };
};

/**
 * Builds the payload for saving a new Dispensing Log record (Tab 1).
 */
export const buildSaveDispensingPayload = ({
  ipData,
  selectedStudyId,
  perSubjectQtyVal,
  totalQtyNeededVal,
  selectedPeriodNo,
  selectedContainerDetails,
  deductions,
}: {
  ipData: any;
  selectedStudyId: string | number;
  perSubjectQtyVal: any;
  totalQtyNeededVal: any;
  selectedPeriodNo: number | null;
  selectedContainerDetails: ContainerItem[];
  deductions: ContainerDeductionState;
}): DispensingSavePayload => {
  const user = GetUserData();
  const containerdetailsPayload = selectedContainerDetails.map((c) => {
    const ded = deductions[c.containernumber];
    const initial = Number(c.initialquantity || 0);
    const deductedqty = Number(ded?.deductedqty || 0);
    const reasonStr = ded?.reason ? String(ded.reason).trim() : "";
    const finalqty = Math.max(0, initial - deductedqty);

    return {
      ipdetailid: Number(c.ipdetailid),
      availableqty: initial,
      deductionreason: reasonStr.length > 0 ? reasonStr : null,
      deductedqty: deductedqty,
      finalqty: finalqty,
    };
  });

  const calculatedTotalQty = containerdetailsPayload.reduce(
    (acc, c) => acc + c.finalqty,
    0
  );

  return {
    ipid: Number(ipData?.ipid),
    studyid: Number(selectedStudyId),
    quantitypersubject: Number(perSubjectQtyVal),
    totalquantityneeded: Number(totalQtyNeededVal || calculatedTotalQty),
    periodid: Number(selectedPeriodNo),
    containerdetails: containerdetailsPayload,
    createdby: Number(user?.userid),
  };
};

/**
 * Builds the payload for updating an existing Dispensing Log record (Tab 1).
 */
export const buildUpdateDispensingPayload = ({
  editId,
  updateReason,
  ipData,
  selectedStudyId,
  perSubjectQtyVal,
  totalQtyNeededVal,
  selectedPeriodNo,
  selectedContainerDetails,
  originalContainerDetails,
  deductions,
}: {
  editId: string | number;
  updateReason: string;
  ipData: any;
  selectedStudyId: string | number;
  perSubjectQtyVal: any;
  totalQtyNeededVal: any;
  selectedPeriodNo: number | null;
  selectedContainerDetails: ContainerItem[];
  originalContainerDetails: any[];
  deductions: ContainerDeductionState;
}): DispensingUpdatePayload => {
  const user = GetUserData();
  const containerdetailsPayload: any[] = [];
  const origList = originalContainerDetails || [];

  // 1. Process items in originalContainerDetails (from DB)
  origList.forEach((orig: any) => {
    const currentlySelectedMatch = selectedContainerDetails.find(
      (c) => String(c.ipdetailid) === String(orig.ipdetailid)
    );

    if (currentlySelectedMatch) {
      // Modify / Keep existing container -> Action "U"
      const ded = deductions[currentlySelectedMatch.containernumber];
      const initial = Number(currentlySelectedMatch.initialquantity || orig.availableqty || 0);
      const deductedqty = Number(ded?.deductedqty || 0);
      const reasonStr = ded?.reason ? String(ded.reason).trim() : "";
      const finalqty = Math.max(0, initial - deductedqty);

      containerdetailsPayload.push({
        containerdetailid: orig.containerdetailid,
        action: "U",
        dispensingid: Number(editId),
        ipdetailid: Number(orig.ipdetailid),
        availableqty: initial,
        deductionreason: reasonStr.length > 0 ? reasonStr : null,
        deductedqty: deductedqty,
        finalqty: finalqty,
      });
    } else {
      // Deselected existing container -> Action "D"
      containerdetailsPayload.push({
        containerdetailid: orig.containerdetailid,
        action: "D",
        dispensingid: Number(editId),
        ipdetailid: Number(orig.ipdetailid),
        availableqty: Number(orig.availableqty || 0),
        deductionreason: orig.deductionreason || null,
        deductedqty: Number(orig.deductedqty || 0),
        finalqty: Number(orig.finalqty || 0),
      });
    }
  });

  // 2. Process newly selected containers not in originalContainerDetails -> Action "I"
  selectedContainerDetails.forEach((c) => {
    const isOriginal = origList.some(
      (orig: any) => String(orig.ipdetailid) === String(c.ipdetailid)
    );

    if (!isOriginal) {
      const ded = deductions[c.containernumber];
      const initial = Number(c.initialquantity || 0);
      const deductedqty = Number(ded?.deductedqty || 0);
      const reasonStr = ded?.reason ? String(ded.reason).trim() : "";
      const finalqty = Math.max(0, initial - deductedqty);

      containerdetailsPayload.push({
        action: "I",
        dispensingid: Number(editId),
        ipdetailid: Number(c.ipdetailid),
        availableqty: initial,
        deductionreason: reasonStr.length > 0 ? reasonStr : null,
        deductedqty: deductedqty,
        finalqty: finalqty,
      });
    }
  });

  const calculatedTotalQty = containerdetailsPayload
    .filter((c) => c.action !== "D")
    .reduce((acc, c) => acc + c.finalqty, 0);

  return {
    dispensingid: Number(editId),
    updatereason: updateReason,
    ipid: Number(ipData?.ipid),
    studyid: Number(selectedStudyId),
    quantitypersubject: Number(perSubjectQtyVal),
    totalquantityneeded: Number(totalQtyNeededVal || calculatedTotalQty),
    periodid: Number(selectedPeriodNo),
    containerdetails: containerdetailsPayload,
    updatedby: Number(user?.userid),
  };
};
